/*
 * Copyright (c) 2023 by N-Ach (Pvt) Ltd
 *
 * ╔═╗ ╔╗    ╔═══╗  ╔╗
 * ║║╚╗║║    ║╔═╗║  ║║
 * ║╔╗╚╝║    ║║ ║╠══╣╚═╗
 * ║║╚╗║║╔══╗║╚═╝║╔═╣╔╗║
 * ║║ ║║║╚══╝║╔═╗║╚═╣║║║
 * ╚╝ ╚═╝    ╚╝ ╚╩══╩╝╚╝
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information
 * of N-Ach (Pvt) Ltd. You shall not disclose such Confidential
 * Information and shall use it only in accordance with the terms
 * of the license agreement you entered into with N-Ach (Pvt) Ltd.
 *
 *
 * @Author Dilhari Jayathunga
 * @Date 25/5/2023
 */

import { Provider } from 'react-redux';
import './App.css';
import 'react-toastify/dist/ReactToastify.min.css';
import { StyledEngineProvider, ThemeProvider } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import { RootStore } from './Store/RootStore';
import createAppTheme from './Theme/Theme';
import PageRoutes from './Routes/PageRoutes';
import { SessionTimeout } from './Components/Timeout/SessionTimeout';
import React from 'react';

function App() {
  const [isSignedIn, setIsSignedIn] = React.useState(false);
  const [themeMode, setThemeMode] = React.useState(() => {
    if (typeof window === 'undefined') {
      return 'light';
    }

    return window.localStorage.getItem('pos-theme') || 'light';
  });

  React.useEffect(() => {
    const getIsSignedIn = () => {
      if (typeof window !== 'undefined') {
        const signed = sessionStorage.getItem('isSignedIn');
        setIsSignedIn(signed);
      }
    };
    getIsSignedIn();
  }, [isSignedIn]);

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('pos-theme', themeMode);
      document.body.dataset.theme = themeMode;
      document.documentElement.setAttribute('data-theme', themeMode);
    }
  }, [themeMode]);

  React.useEffect(() => {
    const handleThemeToggle = (event) => {
      setThemeMode((prev) => event?.detail?.themeMode || (prev === 'dark' ? 'light' : 'dark'));
    };

    window.addEventListener('pos:theme-toggle', handleThemeToggle);
    return () => window.removeEventListener('pos:theme-toggle', handleThemeToggle);
  }, []);

  const toggleTheme = () => {
    if (typeof window !== 'undefined') {
      const nextMode = themeMode === 'dark' ? 'light' : 'dark';
      window.dispatchEvent(new CustomEvent('pos:theme-toggle', { detail: { themeMode: nextMode } }));
    }
  };

  const theme = React.useMemo(() => createAppTheme(themeMode), [themeMode]);

  return (
    <Provider store={RootStore}>
      <ThemeProvider theme={theme}>
        <StyledEngineProvider injectFirst>
          <ToastContainer hideProgressBar theme={themeMode === 'dark' ? 'dark' : 'colored'} autoClose={1000} limit={2} />
          <PageRoutes themeMode={themeMode} onThemeToggle={toggleTheme} />
          {isSignedIn && <SessionTimeout />}
        </StyledEngineProvider>
      </ThemeProvider>
    </Provider>
  );
}

export default App;
